import {
  memo, useState, useCallback, useEffect, useMemo, useContext, type FunctionComponent, type MouseEventHandler
} from 'react';
import map from 'lodash/map';
import size from 'lodash/size';
import isNil from 'lodash/isNil';
import filter from 'lodash/filter';
import endsWith from 'lodash/endsWith';
import { Link as RouterLink } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import type { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
// Skillmore UI Components
import { type ExportFormat } from '@empathco/ui-components/src/models/exportFormat';
import { getName } from '@empathco/ui-components/src/helpers/strings';
import { injectParams } from '@empathco/ui-components/src/helpers/path';
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
import CloseIconButton from '@empathco/ui-components/src/elements/CloseIconButton';
import TagLabel from '@empathco/ui-components/src/elements/TagLabel';
import YesNoButtonGroup from '@empathco/ui-components/src/elements/YesNoButtonGroup';
import ExportButton from '@empathco/ui-components/src/elements/ExportButton';
import CardTitle from '@empathco/ui-components/src/elements/CardTitle';
import CardSection from '@empathco/ui-components/src/elements/CardSection';
import CardFooter from '@empathco/ui-components/src/elements/CardFooter';
import FetchFailedAlert from '@empathco/ui-components/src/elements/FetchFailedAlert';
import LoadingPlaceholder from '@empathco/ui-components/src/elements/LoadingPlaceholder';
// local imports
import { OpportunityStatus, SkillActivity, EmployeeActivityStatus } from '../graphql/types';
import {
  DevPlanProgress, DevPlanDetails, EmployeeDevPlanDetails, EmployeeDevPlanProgress,
  DevPlanTarget, DevPlanCourse, DevPlanAdvisor, DevPlanOpportunity
} from '../graphql/customTypes';
import useCustomerSettings from '../config/customer';
import { PATH_MY_DEV_PLAN, PATH_MY_SKILLS } from '../config/paths';
import { getEmployeeActivities } from '../helpers/graphql';
import { RouterLinkState } from '../helpers/routerParams';
import { GlobalContext } from '../context/global';
import useExport from '../hooks/useExport';
import useEmployeeDevPlanActions from '../hooks/useEmployeeDevPlanActions';
import DevPlanAvatar from '../elements/DevPlanAvatar';
import DevPlanActionSnackbars from '../elements/DevPlanActionSnackbars';
import CohortAvatar from '../elements/CohortAvatar';
import TbItemChip from '../elements/TbItemChip';
import LargeButton from '../elements/LargeButton';
import DevPlanTimestampStatus from '../elements/DevPlanTimestampStatus';
import EmployeeProgressDetails from '../elements/EmployeeProgressDetails';
import CoursesAndAdvisorsGrid from '../v3/CoursesAndAdvisorsGrid';
// SCSS imports
import { header, content, completed, details, detailsButton } from './EmployeeProgressPopup.module.scss';

const contentSx: SxProps<Theme> = { py: 1 } as const;
const linkState: RouterLinkState = { viewDevPlans: true };

const EMPTY_COURSES: DevPlanCourse[] = [];
const EMPTY_ADVISORS: DevPlanAdvisor[] = [];
const EMPTY_OPPORTUNITIES: DevPlanOpportunity[] = [];

type EmployeeProgressPopupProps = {
  readonly individual?: boolean;
  readonly isMyPlan?: boolean;
  readonly supervisor?: boolean | null;
  readonly employeeName?: string;
  readonly employee?: DevPlanProgress | EmployeeDevPlanProgress | null;
  readonly devplan?: DevPlanDetails | EmployeeDevPlanDetails | null;
  readonly activities?: SkillActivity[] | null;
  readonly pending?: boolean | null;
  readonly failed?: boolean | null;
  readonly route?: string | null;
  readonly isOpen?: boolean;
  readonly onClose?: MouseEventHandler<HTMLButtonElement>;
  readonly onExport?: (format: ExportFormat, title: string) => Promise<number | boolean> | null;
  readonly exportDisabled?: boolean;
  // for Storybook only
  readonly testAction?: number;
}

const EmployeeProgressPopup: FunctionComponent<EmployeeProgressPopupProps> = ({
  individual = false,
  isMyPlan = false,
  supervisor = false,
  employeeName,
  employee,
  devplan,
  activities: selectedActivities,
  pending = false,
  failed = false,
  route,
  isOpen = false,
  onClose,
  onExport,
  exportDisabled = false,
  testAction
  // eslint-disable-next-line complexity, max-statements, max-lines-per-function
}) => {
  const { HAS_COURSES, HAS_MENTORING, HAS_DEV_PLAN_OPPORTUNITIES } = useCustomerSettings();

  const { user: { data: user } } = useContext(GlobalContext);
  const { id: devplan_id, title, created_at, updated_at, owner, job, opportunity } = devplan || {};
  const { skills: emplSkills } = employee || {};
  const firstName = getName((employee as DevPlanProgress)?.first_name);
  const { completed_courses, completed_advisors, completed_opportunities, activities: completed_activities } =
    (!individual && (employee as DevPlanProgress)) || {};
  const { is_active } = (individual && (devplan as EmployeeDevPlanDetails)) || {};
  const { courses, advisors, opportunities, activities } = (individual && (employee as EmployeeDevPlanProgress)) || {};
  const targetTitle = job?.title || opportunity?.title;
  const target: DevPlanTarget | null = (job && 'job') || (opportunity && 'opportunity') || null;

  const titleValues = useMemo(() => ({
    name: firstName,
    endsWithS: endsWith(firstName, 's')
  }), [firstName]);

  const {
    pendingAction,
    handleActivation,
    activatePending,
    activateFailed,
    handleCompleteCourse,
    courseCompletingId,
    completeCourseFailed,
    handleSelectActivity,
    activitySelectingId,
    handleCompleteActivity,
    activityCompletingId,
    activityFailed,
    handleCompleteAdvisor,
    advisorCompletingId,
    completeAdvisorFailed,
    handleRequestAdvisor,
    advisorRequestingId,
    requestFailed
  } = useEmployeeDevPlanActions({ devplan_id, is_active, advisors, testAction });

  const completedCourses = useMemo(() => (!HAS_COURSES && EMPTY_COURSES) || (
    individual ? filter(courses, 'is_complete') : completed_courses || EMPTY_COURSES
  ), [courses, completed_courses, individual, HAS_COURSES]);
  const completedAdvisors = useMemo(() => (!HAS_MENTORING && EMPTY_ADVISORS) || (
    individual ? filter(advisors, 'is_complete') : completed_advisors || EMPTY_ADVISORS
  ), [advisors, completed_advisors, individual, HAS_MENTORING]);
  const completedOpportunities = useMemo(() => (!HAS_DEV_PLAN_OPPORTUNITIES && EMPTY_OPPORTUNITIES) || (individual
    ? filter(opportunities, ({ status, archived_at }) => status === OpportunityStatus.archived && Boolean(archived_at))
    : completed_opportunities || EMPTY_OPPORTUNITIES
  ), [opportunities, completed_opportunities, individual, HAS_DEV_PLAN_OPPORTUNITIES]);
  const completedActivities = useMemo(() => getEmployeeActivities(
    individual ? filter(activities, ['status', EmployeeActivityStatus.completed]) : completed_activities
  ), [activities, completed_activities, individual]);

  const [mounted, setMounted] = useState(isOpen);
  useEffect(() => {
    if (isOpen) setMounted(true);
  }, [isOpen]);

  const transitionProps = useMemo(() => ({ onExited: () => {
    setMounted(false);
  } }), []);

  const getExport = useCallback((format: ExportFormat, _token: string) => {
    if (exportDisabled || !onExport) return null;
    return onExport(format, '');
  }, [exportDisabled, onExport]);

  const exp = useExport(getExport);

  const link = useMemo(() => devplan_id && individual && !supervisor && user?.id && user.id === owner?.id
    ? injectParams(PATH_MY_DEV_PLAN, { plan_id: devplan_id }) : undefined,
    [devplan_id, user?.id, owner?.id, individual, supervisor]
  );

  const hasCompletedItems = (!individual || is_active) && (
    size(completedCourses) >= 1 || size(completedAdvisors) >= 1 || size(completedOpportunities) >= 1 ||
    size(completedActivities) >= 1
  );

  const notReady = Boolean(failed || pending) || !employee || !devplan;

  const contentPanel = !onClose || mounted ? (notReady && (
    <Box pt={onClose ? 7.5 : undefined}>
      {(failed && <FetchFailedAlert flat/>) || <LoadingPlaceholder flat/>}
    </Box>
  )) || (
    <>
      {individual ? (
        <>
          <CardSection top>
            <Box
                display="flex"
                flexWrap="wrap"
                alignItems="center"
                pt={{ xs: 2, sm: 4.5 }}
                rowGap={2}
                columnGap={2}
            >
              <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  columnGap={2}
              >
                <DevPlanAvatar/>
                <Typography variant="h3">
                  {title}
                </Typography>
              </Box>
              {supervisor ? (
                <TagLabel variant={is_active ? 'active' : 'inactive'}/>
              ) : (
                <>
                  <YesNoButtonGroup
                      small
                      value={Boolean(is_active)}
                      yesLabel="common.tag.active"
                      noLabel="common.tag.inactive"
                      onChange={handleActivation}
                      disabled={pendingAction}
                      pendingValue={activatePending ? Boolean(is_active) : undefined}
                      sx={{ ml: link ? { xs: 'auto', sm: 3.5 } : 3.5 }}
                  />
                  {link ? (
                    <Button
                        color="primary"
                        variant="outlined"
                        component={RouterLink}
                        to={link}
                        disabled={pending || failed ? true : undefined}
                        className={detailsButton}
                    >
                      <FormattedMessage id="dev_plans.button.view"/>
                    </Button>
                  ) : undefined}
                </>
              )}
            </Box>
            <Box
                display="flex"
                justifyContent="flex-start"
            >
              <DevPlanTimestampStatus
                  compact
                  createdAt={created_at}
                  updatedAt={updated_at}
                  owner={owner}
                  myOwn={isMyPlan || individual ? user?.id === owner?.id : undefined}
              />
            </Box>
            <Divider/>
          </CardSection>
          <CoursesAndAdvisorsGrid
              isMyPlan={isMyPlan}
              courses={HAS_COURSES ? courses : EMPTY_COURSES}
              advisors={HAS_MENTORING ? advisors : EMPTY_ADVISORS}
              opportunities={HAS_DEV_PLAN_OPPORTUNITIES ? opportunities : EMPTY_OPPORTUNITIES}
              activities={selectedActivities}
              disabled={pendingAction}
              notFoundMessage="hr.dev_plan.empty"
              isSelected
              withEmployeeControls={isMyPlan && !supervisor && is_active ? true : undefined}
              withCompletedState={!supervisor && is_active ? true : undefined}
              onCompleteCourse={!supervisor && is_active ? handleCompleteCourse : undefined}
              onSelectActivity={!supervisor && is_active ? handleSelectActivity : undefined}
              onCompleteActivity={!supervisor && is_active ? handleCompleteActivity : undefined}
              onCompleteAdvisor={!supervisor && is_active ? handleCompleteAdvisor : undefined}
              onRequestAdvisor={!supervisor && is_active ? handleRequestAdvisor : undefined}
              courseCompletingId={courseCompletingId}
              activitySelectingId={activitySelectingId}
              activityCompletingId={activityCompletingId}
              advisorCompletingId={advisorCompletingId}
              advisorRequestingId={advisorRequestingId}
          />
        </>
      ) : (
        <CardTitle
            title={isMyPlan ? 'dev_plans.progress' : 'hr.dev_plans.employee_title'}
            values={titleValues}
            compact
            className={onExport ? header : undefined}
            action={onExport ? (
              <ExportButton
                  pending={exp.failed === false}
                  disabled={pendingAction || exportDisabled || exp.failed === false || !exp.enabled}
                  onExport={exp.handleExport}
              />
            ) : undefined}
        />
      )}
      <DialogContent
          sx={contentSx}
          className={!onClose && !hasCompletedItems && !notReady && (!individual || is_active)
            ? content : (hasCompletedItems && completed) || undefined}
      >
        {individual ? (
          <>
            {targetTitle ? (
              <Box pb={2} className={details}>
                <FormattedMessage
                    id="hr.dev_plan.target"
                    values={{ target, title: targetTitle }}
                />
              </Box>
            ) : undefined}
            {is_active ? (
              <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  py={2}
              >
                <CohortAvatar grey/>
                <BoxTypography flexGrow={1} pl={1.25} pr={2} variant="subtitle1">
                  <FormattedMessage
                      id={supervisor ? 'dev_plans.employee_progress' : 'dev_plans.progress'}
                      values={supervisor ? {
                        name: employeeName,
                        endsWithS: endsWith(employeeName, 's')
                      } : undefined}
                  />
                </BoxTypography>
              </Box>
            ) : undefined}
          </>
        ) : (
          <EmployeeProgressDetails
              target={target}
              isMyPlan={isMyPlan}
              employee={employee as DevPlanProgress}
              devplan={devplan as DevPlanDetails}
              route={route}
          />
        )}
        {!individual || is_active ? <Divider/> : undefined}
        {size(emplSkills) >= 1 && (
          <>
            <BoxTypography variant="h6" pt={2.5} pb={1.5}>
              <FormattedMessage id="hr.cohort.skills_criteria"/>
            </BoxTypography>
            <Box display="flex" flexWrap="wrap">
              {map(emplSkills, (skill, index) => (
                <Box key={index} pb={3} pr={3}>
                  <TbItemChip
                      flat
                      item={skill}
                      satisfied={isNil(skill.is_satisfied) || skill.is_satisfied}
                      withLargeLevel
                      disabled={pendingAction}
                      // TODO: onClick - navigate to relevant skill details screen
                  />
                </Box>
              ))}
            </Box>
            <Divider/>
          </>
        )}
        {individual ? (
          <>
            <EmployeeProgressDetails
                target={target}
                individual
                active={is_active}
                employee={employee as DevPlanProgress}
                devplan={devplan as DevPlanDetails}
            />
            {is_active ? <Divider/> : undefined}
          </>
        ) : undefined}
        {!individual || is_active ? (
          <>
            <BoxTypography variant="h6" pt={2.5} pb={hasCompletedItems ? undefined : 1.5}>
              <FormattedMessage id="hr.dev_plan.completed_activity"/>
            </BoxTypography>
            {!hasCompletedItems && (
              <BoxTypography variant="body2">
                <FormattedMessage id="hr.dev_plan.completed_empty"/>
              </BoxTypography>
            )}
          </>
        ) : undefined}
      </DialogContent>
      {hasCompletedItems ? (
        <>
          <CoursesAndAdvisorsGrid
              isMyPlan={isMyPlan}
              courses={completedCourses}
              advisors={completedAdvisors}
              opportunities={completedOpportunities}
              activities={completedActivities}
              isSelected
              withCompletedState
              disabled={pendingAction}
          />
          {onClose ? undefined : <Box className={content}/>}
        </>
      ) : undefined}
      {isMyPlan && !individual ? (
        <CardFooter compact>
          <LargeButton
              title="dev_plans.button.all"
              link={PATH_MY_SKILLS}
              linkState={linkState}
              disabled={pending}
          />
        </CardFooter>
      ) : undefined}
    </>
  ) : undefined;

  return !onClose || mounted ? (
    <>
      {onClose ? (
        <Dialog
            disableEnforceFocus
            maxWidth="lg"
            fullWidth
            scroll="body"
            open={isOpen}
            onClose={pendingAction ? undefined : onClose}
            TransitionProps={transitionProps}
            PaperProps={notReady || (individual && !is_active) ? undefined : { className: content }}
        >
          <CloseIconButton onClick={onClose} disabled={pendingAction}/>
          {contentPanel}
        </Dialog>
      ) : contentPanel}
      {individual && !supervisor ? (
        <DevPlanActionSnackbars
            activateFailed={activateFailed}
            requestFailed={requestFailed}
            completeCourseFailed={completeCourseFailed}
            activityFailed={activityFailed}
            completeAdvisorFailed={completeAdvisorFailed}
        />
      ) : undefined}
    </>
  ) : null;
};

export default memo(EmployeeProgressPopup);
